import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { Inbox, Loader2, ArrowLeft, MapPin, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PassportInbox — instructor view of active learner-passport shares in the
 * marketplace plus any directly sent to this instructor. Supports Phase 1
 * buttons: View Summary, Accept Chat (stub), Save Lead (stub), Pass (stub).
 * Backend sort keeps direct matches + premium tiers on top without hiding
 * free basic shares.
 */
export default function PassportInbox() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/passport/inbox`, { withCredentials: true });
        setItems(r.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="passport-inbox-back-btn" onClick={() => goBack()} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Inbox className="w-5 h-5 text-blue-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">Passport Transfers</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-3" data-testid="passport-inbox-list">
        {items.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-16">
            No learners have shared a passport with you yet.
          </div>
        ) : (
          items.map((it) => (
            <div
              key={it.id}
              data-testid={`passport-inbox-row-${it.id}`}
              className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  data-testid={`passport-inbox-tier-${it.id}`}
                  className="text-[10px] font-bold px-2 py-1 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900"
                >
                  {it.tier_label}
                </span>
                {it.direct_match && (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                    Direct match
                  </span>
                )}
              </div>
              <p className="text-base font-extrabold text-slate-900 dark:text-white">{it.learner_name}</p>
              <p className="text-[12px] text-slate-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" /> {it.target_area || "Area not specified"} · {it.transmission || "any"}
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  data-testid={`passport-inbox-view-${it.id}`}
                  onClick={() => nav(`/p/share/${it.token}`)}
                  size="sm"
                  className="h-9 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold"
                >
                  <ShieldCheck className="w-3 h-3 mr-1" /> View Summary
                </Button>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}
